// @greenlight-spec: 11-observability.md
// @greenlight-spec: 16-api-conventions.md
import { z } from 'zod';
import { AUTOSCALER_NO_CAPACITY_REASONS } from './rollout-blocker.js';
import { InstallCloudProviderSchema, ProviderIacPackageSchema } from './provider-iac-package.js';
import { includePrereleasesFromStoredValue } from './updates.js';
import { UpstreamStatusSchema } from './upstream-status.js';
import { WorkerTaskSummariesSchema } from './worker-tasks.js';
// Reusable latency percentile bucket; used by hotpath_latency sub-fields.
export const LatencyBucketSchema = z.object({
    p50: z.number(),
    p95: z.number(),
    p99: z.number(),
});
// Connection usage for one Postgres server (client backends vs max_connections).
// Used by both `postgres` sub-sections of the health snapshot and returned by
// `DatabaseProvider.getConnectionUsage` for the app-data server. `0/0` is the
// documented "no source" sentinel, never a real reading.
export const PostgresConnectionUsageSchema = z.object({
    active_connections: z.number(),
    max_connections: z.number(),
});
// App-runtime capacity: whether the nodes hosting app workloads can still
// schedule a NEW app pod. Distinct from control-plane health — a cluster whose
// control-plane pod is ready but whose app nodes are request-saturated, or whose
// autoscaler is backing off, cannot ship a new app. Every figure is REQUESTS-based
// (reservations the scheduler honours), not live usage. CPU is millicores (`_m`),
// memory MiB (`_mib`). Utilization and headroom are derived from allocatable +
// requested (see `capacityUtilizationPercent`), so they are not stored. No
// pod/node names, no env, no customer data.
/**
 * The autoscaler bounds configured on the app node pool, read from the cloud's
 * own control plane. Distinct from `app_node_count`, which counts wherever app
 * pods actually run: this is the range the pool may scale within, so a current
 * count sitting at `max_count` reads as a ceiling rather than as coincidence.
 * Read from the cloud, not from the install-input snapshot, so a pool changed
 * outside the installer reports what it is rather than what was recorded.
 */
export const AppPoolNodeRangeSchema = z.object({
    min_count: z.number(),
    max_count: z.number(),
});
export const AppRuntimeCapacitySchema = z.object({
    /** Nodes currently running at least one app pod. `0` means nothing to measure. */
    app_node_count: z.number(),
    /**
     * Additive-optional: snapshots written before the pool read existed carry no
     * key, and `null` is a read that was attempted and unavailable. Both render as
     * "no data" rather than as a pool with no bounds.
     */
    app_pool_range: AppPoolNodeRangeSchema.nullable().optional(),
    cpu: z.object({ allocatable_m: z.number(), requested_m: z.number() }),
    memory: z.object({ allocatable_mib: z.number(), requested_mib: z.number() }),
    /** App pods that are Pending because the scheduler could not place them. */
    pending_unschedulable_count: z.number(),
    /** Deduplicated representative scheduler/autoscaler reasons (bounded, secret-free). */
    reasons: z.array(z.string()),
});
/** One resource's requests on a node, split by whose pods reserved it. */
const NodeRequestsSchema = z.object({
    /** The platform the installer ships: the control plane, the updater, Traefik, cert-manager. */
    greenlight: z.number(),
    /** Pods in `gl-app-*` namespaces. */
    apps: z.number(),
    /** Everything else: the cluster's own system pods and the cloud's add-ons. */
    kubernetes: z.number(),
});
/**
 * `<major>.<minor>.<patch>`, an optional leading `v`, and an optional build
 * suffix (`v1.33.5-gke.1080000`, `v0.9.0-eksbuild.11`, kube-dns's `1.26.4-gke.2`).
 * Nothing else crosses: a version string is the only free-form text the
 * Kubernetes reading carries, so the shape is what keeps it out of the allowlist.
 */
export const KUBERNETES_VERSION_PATTERN = /^v?\d+\.\d+\.\d+(?:[-+][0-9A-Za-z.+-]+)?$/;
export const KUBERNETES_VERSION_MAX_LENGTH = 64;
export function isKubernetesVersionString(value) {
    return (typeof value === 'string' &&
        value.length <= KUBERNETES_VERSION_MAX_LENGTH &&
        KUBERNETES_VERSION_PATTERN.test(value));
}
export const KubernetesVersionStringSchema = z
    .string()
    .max(KUBERNETES_VERSION_MAX_LENGTH)
    .regex(KUBERNETES_VERSION_PATTERN);
export const KUBERNETES_KUBELET_VERSIONS_MAX = 5;
export const KUBERNETES_COMPONENTS_MAX = 12;
export const KUBERNETES_COMPONENT_IMAGE_TAGS_MAX = 2;
/**
 * One add-on the cloud's component table names. `name` is a tolerated
 * snake_case vocabulary, so a cloud adding a component is not a schema change.
 */
export const KubernetesComponentSchema = z.object({
    name: z
        .string()
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/),
    /** Distinct running tags; two while a rollout is in progress. */
    image_tags: z.array(KubernetesVersionStringSchema).max(KUBERNETES_COMPONENT_IMAGE_TAGS_MAX),
    /** EKS only: the add-on version EKS manages. `null` when the read failed; absent off EKS. */
    addon_version: KubernetesVersionStringSchema.nullable().optional(),
});
/** docs/11 §Kubernetes version. */
export const KubernetesVersionsSchema = z.object({
    /** The API server's `/version` `gitVersion`; `null` when the read failed. */
    server_version: KubernetesVersionStringSchema.nullable(),
    /** Absent with `components` when the capacity read failed. Newest first. */
    kubelet_versions: z
        .array(z.object({ version: KubernetesVersionStringSchema, node_count: z.number().int().positive() }))
        .max(KUBERNETES_KUBELET_VERSIONS_MAX)
        .optional(),
    components: z.array(KubernetesComponentSchema).max(KUBERNETES_COMPONENTS_MAX).optional(),
});
/**
 * One node as the scheduler sees it: the machine's capacity, what Kubernetes
 * leaves allocatable after the kubelet's and the OS's own reservations, and the
 * requests the pods on it hold, by owner. Requests-based, like the aggregate
 * above. Carries no node name: nodes are ordered, and the page numbers them.
 */
export const ClusterNodeSchema = z.object({
    /** `apps` for the autoscaled pool the scheduling contract labels; `system` otherwise. */
    pool: z.enum(['system', 'apps']),
    ready: z.boolean(),
    /**
     * `false` while the node takes no new app pods — cordoned, or tainted against
     * them — though it may still be Ready. Absent on rows from before it was read.
     */
    schedulable: z.boolean().optional(),
    cpu: z.object({
        capacity_m: z.number(),
        allocatable_m: z.number(),
        requested_m: NodeRequestsSchema,
    }),
    memory: z.object({
        capacity_mib: z.number(),
        allocatable_mib: z.number(),
        requested_mib: NodeRequestsSchema,
    }),
    /**
     * How many pods the node may hold and how many unfinished pods it holds now.
     * Absent where the node does not report its pod allocatable, and on older rows.
     */
    pods: z.object({ allocatable: z.number(), scheduled: z.number() }).optional(),
    /** The node's kubelet version. Absent when it is not a version, and on older rows. */
    kubelet_version: KubernetesVersionStringSchema.optional(),
});
/**
 * The per-node view summed over the nodes that count as room, with how many
 * there are: the reading the install's page judges, in a form that carries no
 * node. `node_count: 0` is a view in which no node counts, which is no-data.
 */
export const ClusterTotalsSchema = z.object({
    node_count: z.number().int().nonnegative(),
    cpu: z.object({ requested_m: z.number(), allocatable_m: z.number() }),
    memory: z.object({ requested_mib: z.number(), allocatable_mib: z.number() }),
});
/**
 * Whether a node is room a new app pod could use: the scheduler places nothing on
 * a node that is not ready or is taking no new pods, and a node that has not
 * reported what it can allocate (registered before its kubelet posted status)
 * has no reading to add.
 */
export function nodeCountsAsRoom(node) {
    return (node.ready &&
        node.schedulable !== false &&
        node.cpu.allocatable_m > 0 &&
        node.memory.allocatable_mib > 0);
}
/**
 * The per-node view summed over the nodes that count as room. `null` without a
 * node list, or with an empty one, which is a view that was not read.
 */
export function clusterTotals(nodes) {
    if (!nodes || nodes.length === 0)
        return null;
    const counted = nodes.filter(nodeCountsAsRoom);
    const sum = (r) => r.greenlight + r.apps + r.kubernetes;
    return counted.reduce((acc, n) => ({
        node_count: acc.node_count + 1,
        cpu: {
            requested_m: acc.cpu.requested_m + sum(n.cpu.requested_m),
            allocatable_m: acc.cpu.allocatable_m + n.cpu.allocatable_m,
        },
        memory: {
            requested_mib: acc.memory.requested_mib + sum(n.memory.requested_mib),
            allocatable_mib: acc.memory.allocatable_mib + n.memory.allocatable_mib,
        },
    }), {
        node_count: 0,
        cpu: { requested_m: 0, allocatable_m: 0 },
        memory: { requested_mib: 0, allocatable_mib: 0 },
    });
}
function wholeCluster(cluster) {
    return Array.isArray(cluster) ? clusterTotals(cluster) : (cluster ?? null);
}
/**
 * The cluster's reservations over the nodes that count as room, when the
 * per-node view or totals built from it are there, and from the app-node
 * aggregate when they are not — the older reading, which covers only nodes that
 * run an app. `null` when nothing measured was read, including a view with no
 * node that counts, so an unmeasured cluster never totals to a healthy zero.
 */
export function clusterCapacityTotals(cluster, aggregate) {
    const whole = wholeCluster(cluster);
    if (whole) {
        return whole.node_count > 0 ? { cpu: whole.cpu, memory: whole.memory } : null;
    }
    if (!aggregate || aggregate.cpu.allocatable_m <= 0 || aggregate.memory.allocatable_mib <= 0) {
        return null;
    }
    return {
        cpu: { requested_m: aggregate.cpu.requested_m, allocatable_m: aggregate.cpu.allocatable_m },
        memory: {
            requested_mib: aggregate.memory.requested_mib,
            allocatable_mib: aggregate.memory.allocatable_mib,
        },
    };
}
export function appPoolCapacity(nodes) {
    if (!nodes)
        return null;
    const pool = nodes.filter((node) => node.pool === 'apps');
    const room = pool.filter(nodeCountsAsRoom);
    const totals = room.length > 0 ? clusterCapacityTotals(room, null) : null;
    return totals ? { ...totals, node_count: pool.length, room_node_count: room.length } : null;
}
/** What an app pod reserves when its manifest declares no `compute:` block (docs/08). */
export const APP_POD_BASELINE_REQUESTS = { cpu_m: 25, memory_mib: 128 };
/**
 * The cluster autoscaler adds a node only when a pending pod fits on no node
 * running now, so headroom is per node: a pod has to fit inside one node's free
 * CPU, free memory and free pod slots, never in the cluster's total. `null` when
 * no node can take a pod.
 */
export function clusterHeadroom(nodes) {
    const reserved = (r) => r.greenlight + r.apps + r.kubernetes;
    let baselinePods = 0;
    let roomiest = null;
    const mostFree = { cpu_m: 0, memory_mib: 0 };
    for (const [index, node] of (nodes ?? []).entries()) {
        if (!nodeCountsAsRoom(node))
            continue;
        const cpu = Math.max(0, node.cpu.allocatable_m - reserved(node.cpu.requested_m));
        const memory = Math.max(0, node.memory.allocatable_mib - reserved(node.memory.requested_mib));
        const slots = node.pods
            ? Math.max(0, node.pods.allocatable - node.pods.scheduled)
            : Number.POSITIVE_INFINITY;
        const fits = Math.min(Math.floor(cpu / APP_POD_BASELINE_REQUESTS.cpu_m), Math.floor(memory / APP_POD_BASELINE_REQUESTS.memory_mib), slots);
        baselinePods += fits;
        if (slots > 0) {
            mostFree.cpu_m = Math.max(mostFree.cpu_m, cpu);
            mostFree.memory_mib = Math.max(mostFree.memory_mib, memory);
        }
        if (!roomiest || fits > roomiest.fits || (fits === roomiest.fits && cpu > roomiest.cpu)) {
            roomiest = { index, fits, cpu };
        }
    }
    return roomiest
        ? { baseline_pods: baselinePods, roomiest_index: roomiest.index, most_free: mostFree }
        : null;
}
/** The tighter of CPU and memory, as a percent of allocatable — the one-number summary. */
export function clusterCapacityPeak(totals) {
    return Math.max(capacityUtilizationPercent(totals.cpu.requested_m, totals.cpu.allocatable_m), capacityUtilizationPercent(totals.memory.requested_mib, totals.memory.allocatable_mib));
}
/**
 * Percent of allocatable reserved by requests, to one decimal. `0` when
 * there is nothing to compare against; `100` when requests exist but allocatable
 * is `0` — reservations against no capacity leave no headroom for anything.
 */
export function capacityUtilizationPercent(requested, allocatable) {
    if (allocatable > 0)
        return Math.round((requested / allocatable) * 1000) / 10;
    return requested > 0 ? 100 : 0;
}
/** The higher of CPU and memory request-utilization — the binding constraint. */
export function peakCapacityUtilization(cap) {
    return Math.max(capacityUtilizationPercent(cap.cpu.requested_m, cap.cpu.allocatable_m), capacityUtilizationPercent(cap.memory.requested_mib, cap.memory.allocatable_mib));
}
/** True only for actionable pressure — never for `ok` or `unknown` (no data). */
export function isCapacityPressure(level) {
    return level === 'warning' || level === 'critical';
}
/** Request-utilization thresholds (percent of allocatable). */
export const APP_CAPACITY_WARN_UTILIZATION = 80;
export const APP_CAPACITY_CRITICAL_UTILIZATION = 95;
// Reasons that mean the cluster autoscaler will not add app-node capacity on its own.
const AUTOSCALER_BACKOFF_HINT = new RegExp([...AUTOSCALER_NO_CAPACITY_REASONS, 'max node', 'quota', 'backoff'].join('|'), 'i');
/**
 * Single source of truth for app-runtime capacity pressure: the Internal Health
 * page calls it to degrade its verdict and show IT the exact reason.
 * `null`/absent capacity (read unavailable) assesses as `unknown` — the page
 * renders it as "no data" rather than a false alarm, and it never counts as
 * pressure (see `isCapacityPressure`).
 */
export function assessAppRuntimeCapacity(cap, cluster) {
    if (!cap)
        return { level: 'unknown', reason: '' };
    // With the per-node view, or the totals a fleet report builds from it, the
    // denominator is every node a new pod could use (`nodeCountsAsRoom`): a node
    // running no app is still room. Without either — a report or a row from
    // before them — the aggregate is the only reading there is.
    const whole = wholeCluster(cluster);
    const totals = whole ? clusterCapacityTotals(whole, cap) : null;
    const util = totals ? clusterCapacityPeak(totals) : whole ? null : peakCapacityUtilization(cap);
    const scope = totals ? 'Cluster requests' : 'Requests on the nodes running apps';
    // Autoscaler backoff only matters while an app pod is actually unschedulable:
    // Kubernetes events outlive the incident (~1h TTL), so a backoff reason with no
    // pending pod is history, not pressure.
    if (cap.pending_unschedulable_count > 0) {
        const autoscalerReason = cap.reasons.find((r) => AUTOSCALER_BACKOFF_HINT.test(r));
        if (autoscalerReason) {
            return {
                level: 'critical',
                reason: `New app pods cannot schedule and the autoscaler is not adding capacity: ${autoscalerReason}`,
            };
        }
        const reason = cap.reasons[0] ?? 'Insufficient capacity';
        const level = util !== null && util >= APP_CAPACITY_CRITICAL_UTILIZATION ? 'critical' : 'warning';
        return {
            level,
            reason: `${cap.pending_unschedulable_count} new app pod(s) cannot schedule: ${reason}`,
        };
    }
    // Nothing scheduled and nothing waiting: there is no pool state to judge, so
    // report no-data rather than `ok`. Zero allocatable would otherwise divide out
    // to 0% and badge as healthy — a false green on the one signal IT reads to
    // decide whether a new app can ship.
    // The per-node view measures every node, so a cluster with no app yet is still
    // a reading rather than an absence — unless no node has reported what it can
    // allocate, which is as unmeasured as an empty aggregate.
    if (!totals && cap.app_node_count === 0) {
        return {
            level: 'unknown',
            reason: 'No node is running an app yet — no capacity to report.',
        };
    }
    if (util === null) {
        return {
            level: 'unknown',
            reason: 'No ready node has reported what it can allocate yet.',
        };
    }
    if (util >= APP_CAPACITY_CRITICAL_UTILIZATION) {
        return {
            level: 'critical',
            reason: `${scope} at ${Math.round(util)}% of allocatable — no headroom for a new app pod.`,
        };
    }
    if (util >= APP_CAPACITY_WARN_UTILIZATION) {
        return {
            level: 'warning',
            reason: `${scope} at ${Math.round(util)}% of allocatable.`,
        };
    }
    return { level: 'ok', reason: '' };
}
// Coarse per-resource availability reported by the active cloud provider's
// resource-health probes (`checkResourceHealth`, docs/18). Cloud-neutral: the
// snapshot keys resources by platform role (compute, vault, …), never by a
// vendor product name.
export const ProviderResourceHealthStatusSchema = z.enum(['available', 'degraded', 'unavailable']);
// One crash occurrence, derived entirely from Kubernetes pod container
// termination metadata (docs/11 §Snapshot data model `crashes[]`): the failing
// component, its app id (null for non-app components), the image the container
// ran, the kubelet termination reason (`Error`, `OOMKilled`, …) and exit code,
// when it terminated, and a stable fingerprint of component + reason + image.
// No logs, stack traces, or app payloads — only termination metadata. Non-app
// components carry null app_id/exit_code rather than omitting the keys, so the
// strict outbound report schema stays simple.
/**
 * Reported in place of a vendor domain when the upstream host is not a public
 * registrable domain — an IP literal, a single-label name, a private or
 * reserved address, or an internal suffix. Carries no part of the host.
 */
export const VENDOR_DOMAIN_PRIVATE = 'private';
/**
 * Upper bound on distinct custom vendors one report carries. Exceeding it sets
 * `custom_truncated`, which suppresses the receiver's delete pass — a silently
 * capped tail would otherwise read as deregistration on every report.
 */
export const HEALTH_INTEGRATIONS_CUSTOM_CAP = 200;
// A registrable domain (eTLD+1, lowercase, at least one dot) or the sentinel.
// Never a full host: the subdomain is the customer-specific label.
const vendorDomainSchema = z
    .string()
    .max(253)
    .regex(/^(?:private|[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+)$/, 'must be a lowercase registrable domain or the `private` sentinel');
const integrationCount = () => z.number().int().nonnegative();
export const HealthIntegrationCustomEntrySchema = z.object({
    vendor_domain: vendorDomainSchema,
    transport: z.enum(['http', 'database']),
    engine: z.string().nullable(),
    auth_mode: z.string(),
    count: integrationCount(),
});
/**
 * Inventory of what this install brokers to (docs/11 §Fleet Health Reporting).
 * Counts plus vendor identity — catalog key for catalog-backed registrations,
 * registrable domain for catalog-less ones. The customer-authored integration
 * name is never carried; it is free text, which the report allowlist forbids.
 */
export const HealthIntegrationsSchema = z.object({
    total: integrationCount(),
    count_by_status: z.record(integrationCount()),
    count_by_delivery_mode: z.record(integrationCount()),
    catalog: z.record(integrationCount()),
    custom: z.array(HealthIntegrationCustomEntrySchema),
    custom_truncated: z.boolean(),
});
export const HealthCrashEntrySchema = z.object({
    component: z.string(),
    app_id: z.string().nullable(),
    /**
     * The app's slug, so a reader of the fleet can name what crashed without
     * holding a copy of the customer's app catalogue. Additive and optional:
     * reports written before it exists carry only the id.
     */
    app_slug: z.string().nullable().optional(),
    image: z.string(),
    reason: z.string(),
    exit_code: z.number().int().nullable(),
    occurred_at: z.string().datetime({ offset: true }),
    fingerprint: z.string(),
});
// The newest failed updater run, derived from `update_history` (docs/26
// §Status reporting). Everything here is a bounded result code the updater's
// own writer sanitized — never command output, plans, or manifests — and the
// collector re-clamps defensively, so `unknown` is the worst a malformed row
// can render.
export const UpdaterFailureSchema = z.object({
    at: z.string().datetime({ offset: true }),
    phase: z.string(),
    release_version: z.string(),
    reason: z.string(),
    installer_command: z.string().nullable(),
});
/**
 * Which install a snapshot describes. Exported so the fleet report can rebuild
 * it: the report needs the plain object to stay strict, while reads of stored
 * rows go through the adapter below.
 */
export const HealthInstallationSchema = z.object({
    installation_id: z.string(),
    region: z.string(),
    // `null` until the updater stack is enabled: an install with no updater has
    // never expressed a posture, and reporting `false` would claim one.
    include_prereleases: z.boolean().nullable(),
    // schema_version >= 3. `null` on a local or fake provider, where there is no
    // cloud to name; optional so snapshots stored before v3 still parse.
    cloud_provider: InstallCloudProviderSchema.nullable().optional(),
});
/** The sentinel pre-rename rows used when the updater injected nothing. */
const UNREPORTED = 'unknown';
/**
 * Rows written before `include_prereleases` named the posture `release_channel`
 * (`stable` | `dev` | `unknown`). They stay readable for the whole retention
 * window, and Internal Health hard-parses the newest one, so the shape is
 * translated on read rather than failing the row. `unknown` becomes `null`, not
 * `false` — that row reported no posture, and `false` would invent one.
 */
function adaptStoredInstallation(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return value;
    const row = value;
    if ('include_prereleases' in row)
        return row;
    const { release_channel: stored, ...rest } = row;
    return {
        ...rest,
        include_prereleases: typeof stored === 'string' && stored !== UNREPORTED
            ? includePrereleasesFromStoredValue(stored)
            : null,
    };
}
// Authoritative field list mirrors the health_snapshots JSON payload in
// docs/11-observability.md §Snapshot data model. schema_version allows
// additive payload evolution without breaking existing readers.
/** One control-plane Deployment as the cluster reports it (docs/46 §Control-plane topology). */
export const ControlPlaneTierSchema = z.object({
    ready_replicas: z.number(),
    desired_replicas: z.number(),
});
/**
 * What one control-plane container is using against the limit it is actually
 * running under. `measured_by` names the measurement, because a cgroup figure
 * and a process RSS are not interchangeable, and a `null` usage beside a real
 * limit is a reading that could not be taken rather than an idle container. The
 * field is not called `source`, which the report allowlist forbids by name.
 */
export const ControlPlaneReplicaSchema = z.object({
    tier: z.enum(['serve', 'worker']),
    replica_id: z.string(),
    /** Container start identity: every counter on this entry resets with it. */
    incarnation: z.string().nullable(),
    memory: z.object({
        usage_bytes: z.number().nullable(),
        limit_bytes: z.number().nullable(),
        measured_by: z.enum(['metrics_server', 'cgroup', 'process', 'unavailable']),
    }),
    cpu: z.object({
        usage_cores: z.number().nullable(),
        limit_cores: z.number().nullable(),
        throttled_ratio: z.number().nullable(),
        measured_by: z.enum(['metrics_server', 'cgroup', 'process', 'unavailable']),
    }),
    restart_count: z.number(),
    /** The kubelet's record of the previous container: how an OOM kill survives its restart. */
    last_termination: z
        .object({
        reason: z.string().nullable(),
        exit_code: z.number().nullable(),
        finished_at: z.string().nullable(),
        incarnation: z.string().nullable(),
    })
        .optional(),
});
/**
 * The services Greenlight runs in the cluster beside the primary server and the
 * async worker (docs/46 §Two planes, one request path).
 */
export const PLATFORM_SERVICE_KEYS = ['renderer', 'ingress', 'certificates'];
export const PlatformServiceReplicaSchema = ControlPlaneReplicaSchema.omit({ tier: true });
export const PlatformServiceSchema = z.object({
    key: z.enum(PLATFORM_SERVICE_KEYS),
    ready_replicas: z.number(),
    desired_replicas: z.number(),
    replicas: z.array(PlatformServiceReplicaSchema),
    /**
     * The service's Deployments could not be read. Its counts carry no
     * information, so the page reports it as unreadable rather than as down.
     */
    read_error: z.boolean().optional(),
});
/**
 * How old the newest observation is, measured on the server's clock rather than
 * the reader's. A reachable endpoint says nothing about when the collector last
 * ran: it serves whatever the newest row is, so the age travels with it. Past
 * `threshold_s` the reading is stale and cannot establish current health, which
 * is the case a green row written by a since-dead collector would otherwise win.
 * `collected_at: null` is an install before its first collection, which is not
 * stale — nothing has stopped arriving yet.
 */
export const HealthFreshnessSchema = z.object({
    server_time: z.string().datetime({ offset: true }),
    collected_at: z.string().datetime({ offset: true }).nullable(),
    age_s: z.number().nullable(),
    stale: z.boolean(),
    threshold_s: z.number(),
});
/**
 * Three missed collection ticks (22-nfrs.md §Resource Limits): wide enough to
 * absorb one slow pass and a worker restart, narrow enough that an operator
 * reading the page during an incident is not reading yesterday.
 */
export const HEALTH_SNAPSHOT_STALE_AFTER_SECONDS = 180;
/**
 * Memory pressure across the control plane, judged per replica against the limit
 * that replica is running under.
 *
 * The worst replica decides, because the failure this exists to catch is one
 * container walking up to its own ceiling: averaging a hot serve pod with an
 * idle worker reports a comfortable middle and badges green right up to the OOM
 * kill. A replica whose usage could not be read is skipped rather than counted
 * as healthy, and a set with nothing readable is `unknown` — the one answer that
 * is never "fine".
 */
export function assessControlPlaneMemory(replicas) {
    const measured = (replicas ?? []).flatMap((replica) => {
        const { usage_bytes: usage, limit_bytes: limit } = replica.memory;
        if (usage === null || limit === null || limit <= 0)
            return [];
        return [{ replica, utilization: (usage / limit) * 100 }];
    });
    if (measured.length === 0)
        return { level: 'unknown', reason: '' };
    const worst = measured.reduce((a, b) => (b.utilization > a.utilization ? b : a));
    const percent = Math.round(worst.utilization);
    if (worst.utilization >= APP_CAPACITY_CRITICAL_UTILIZATION) {
        return {
            level: 'critical',
            reason: `${worst.replica.replica_id} is at ${percent}% of its memory limit`,
        };
    }
    if (worst.utilization >= APP_CAPACITY_WARN_UTILIZATION) {
        return {
            level: 'warning',
            reason: `${worst.replica.replica_id} is at ${percent}% of its memory limit`,
        };
    }
    return { level: 'ok', reason: '' };
}
/**
 * The closed set of charted series. Named on the contract rather than validated
 * in the service so a caller can discover them, and so an unknown name is
 * refused at the boundary rather than deep in a query.
 */
export const HEALTH_SERIES_METRICS = [
    'control_plane_memory',
    'control_plane_cpu',
    'control_plane_cpu_throttled',
    'control_plane_restarts',
    'control_plane_restart_delta',
    'availability_ready_replicas',
    'queue_pending',
    'queue_oldest_pending_age',
    'queue_failed_last_1h',
    'queue_locked_over_duration',
    'queue_exhausted',
    'queue_abandoned',
    'postgres_connections',
    'cluster_cpu_requested',
    'cluster_memory_requested',
    'app_pods_unschedulable',
    'app_pool_cpu_requested',
    'app_pool_memory_requested',
    'hotpath_p95',
    'hotpath_p99',
    'hotpath_requests',
    'hotpath_server_errors',
    'hotpath_client_errors',
    'release_running',
];
export const HealthSeriesMetricSchema = z.enum(HEALTH_SERIES_METRICS);
/** Bucket widths the dashboard's 1h / 24h / 7d / 30d ranges map onto. */
export const HealthSeriesStepSchema = z.enum(['1m', '5m', '1h', '2h']);
/**
 * How a bucket reduces the observations inside it. Stated on the wire because
 * it changes what the numbers mean: a gauge carries an average and a peak, a
 * counter is summed, and a percentile reports the worst per-minute value it saw
 * rather than an average of percentiles, which would be a fabrication with a
 * plausible shape.
 */
export const HealthSeriesAggregationSchema = z.enum(['average_and_peak', 'sum', 'worst_observed']);
export const HealthSeriesBucketSchema = z.object({
    t: z.string().datetime({ offset: true }),
    avg: z.number().nullable(),
    peak: z.number().nullable(),
    min: z.number().nullable(),
    sum: z.number().nullable(),
    /** The limit in force during the bucket, so a later change cannot reinterpret it. */
    limit: z.number().nullable(),
    /** Zero means nothing was observed in this bucket: a gap, never a zero reading. */
    observations: z.number(),
});
export const HealthSeriesScopeSchema = z.object({
    /** Replica id, tier, or database name; null when the series is installation-wide. */
    scope: z.string().nullable(),
    buckets: z.array(HealthSeriesBucketSchema),
});
export const HealthSeriesSchema = z.object({
    metric: HealthSeriesMetricSchema,
    unit: z.string(),
    aggregation: HealthSeriesAggregationSchema,
    step: HealthSeriesStepSchema,
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    scopes: z.array(HealthSeriesScopeSchema),
    /**
     * Scopes observed in the window but not returned, because a response carries
     * at most a fixed number. The most recently observed are the ones kept.
     */
    omitted_scopes: z.number(),
    /** What the window could have held against what it did, so sparse history says so. */
    coverage: z.object({ expected_buckets: z.number(), observed_buckets: z.number() }),
    /** The oldest retained observation for this metric, or null when there is none. */
    available_from: z.string().datetime({ offset: true }).nullable(),
});
/** The services a per-replica or per-service series can be narrowed to. */
export const HEALTH_SERIES_SERVICES = ['serve', 'worker', ...PLATFORM_SERVICE_KEYS];
export const HealthSeriesServiceSchema = z.enum(HEALTH_SERIES_SERVICES);
export const HealthSeriesQuerySchema = z.object({
    metric: HealthSeriesMetricSchema,
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    step: HealthSeriesStepSchema,
    /** Only the samples recorded for this service; omitted reads every scope. */
    service: HealthSeriesServiceSchema.optional(),
});
/**
 * How long the oldest runnable job may wait before the queue counts as backed
 * up (docs/11 §Queue trouble). The page's verdict and incident detection read
 * the same number, so a badge and an incident never disagree about it.
 */
export const QUEUE_BACKLOG_ALERT_SECONDS = 30;
/** The only incidents recorded (11-observability.md §Observed incidents). */
export const HEALTH_INCIDENT_KINDS = [
    'container_terminated',
    'memory_pressure',
    'readiness',
    'queue_trouble',
    'dependency_degraded',
];
export const HealthIncidentKindSchema = z.enum(HEALTH_INCIDENT_KINDS);
export const HealthIncidentSchema = z.object({
    id: z.string().uuid(),
    kind: HealthIncidentKindSchema,
    /** The service key (`HEALTH_SERIES_SERVICES`); null for an installation-wide condition. */
    tier: z.string().nullable(),
    replica_id: z.string().nullable(),
    /** Set for container kinds only; a condition belongs to no container. */
    incarnation: z.string().nullable(),
    /** When it happened. Only this clock orders the story. */
    occurred_at: z.string().datetime({ offset: true }),
    /** When Greenlight first saw it, often a tick or more later. */
    observed_at: z.string().datetime({ offset: true }),
    /** A bounded code, never a log line. */
    reason: z.string(),
    /** The release running when it happened; null when no observation establishes it. */
    release_version: z.string().nullable(),
    operation_ref: z.record(z.unknown()).nullable(),
    /** Groups similar incidents. Not an identity. */
    fingerprint: z.string(),
    /**
     * `unresolved` is live. `unknown` means it ended without Greenlight observing a
     * recovery, which is different from having seen one.
     */
    status: z.enum(['unresolved', 'recovered', 'unknown']),
    resolved_at: z.string().datetime({ offset: true }).nullable(),
});
/**
 * The previous container's log tail for a termination. `not_captured` covers a
 * condition incident, a pod that moved on before the read, and a read that never
 * succeeded; `expired` is a tail that aged out or was displaced by newer ones.
 */
export const HealthIncidentDiagnosticsSchema = z.object({
    status: z.enum(['available', 'expired', 'not_captured']),
    captured_at: z.string().datetime({ offset: true }).nullable(),
    expires_at: z.string().datetime({ offset: true }).nullable(),
    byte_length: z.number().nullable(),
    /** Older output existed that the capture does not include. */
    truncated: z.boolean().nullable(),
});
export const HealthIncidentDetailSchema = HealthIncidentSchema.extend({
    diagnostics: HealthIncidentDiagnosticsSchema,
});
/**
 * An incident is listed when it was live at any point in the window. Both
 * bounds are optional: omitted, the window is the last 90 days, which covers
 * everything retained.
 */
export const HealthIncidentsQuerySchema = z.object({
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
});
export const HealthSnapshotSchema = z.object({
    schema_version: z.number(),
    collected_at: z.string().datetime({ offset: true }),
    installation: z.preprocess(adaptStoredInstallation, HealthInstallationSchema),
    versions: z.object({
        /**
         * Identity of the whole install: the chart-injected product SemVer and the
         * product-repo commit baked into the server image. Optional because
         * snapshots stored before they existed are still read back, not because a
         * live collector may omit them — it writes the `unknown` sentinel instead.
         * `control_plane_image` carries the same commit and stays for those older
         * rows; it is a source SHA, never an image digest. `updater.installed_version`
         * reports the same SemVer as updater state; this one is install identity, and
         * is present whether or not the updater stack is deployed.
         */
        release_version: z.string().optional(),
        source_commit: z.string().optional(),
        control_plane_image: z.string(),
        /**
         * Cloud-neutral identity of the packaged installer this release was
         * applied from (schema_version >= 3). Optional so pre-v3 rows parse; a v3
         * collector always writes it, reporting `{ kind: "unknown" }` when it has
         * no digest to vouch for.
         */
        provider_iac_package: ProviderIacPackageSchema.optional(),
        /**
         * Retired at schema_version 3 in favour of `provider_iac_package`, which
         * names every cloud's installer rather than only Azure's. Still accepted
         * because stored rows inside the retention window carry it, and the fleet
         * report's acceptance floor sits far below v3. Fleet readers reach it only
         * through the legacy adapter, never directly.
         */
        bicep_template: z.string().optional(),
        db_migration: z.string(),
        opa_bundle: z.string(),
        updater_image: z.string(),
        skills_template: z.string(),
        agent_plugin_template: z.string(),
        /**
         * What the cluster runs, read live. Absent on rows from before it existed,
         * `null` off-cluster; both read as "no data", never as a version.
         */
        kubernetes: KubernetesVersionsSchema.nullable().optional(),
    }),
    queue: z.object({
        pending_by_kind: z.record(z.number()),
        oldest_pending_age_s: z.number(),
        failed_last_1h: z.number(),
        /**
         * Work that stopped progressing, which pending depth cannot show: a job a
         * worker still holds is not pending, and one that spent its last attempt
         * without recording an error never reaches `failed_last_1h`.
         */
        locked_over_duration: z.number().optional(),
        /**
         * Locks whose owner is confirmed gone. `null` until the lock-recovery path
         * can supply ownership evidence — duration alone is not death, and a
         * confident `0` would claim a check nothing performs.
         */
        abandoned: z.number().nullable().optional(),
        /**
         * Jobs that spent their last attempt in the past 24 hours. Windowed rather
         * than lifetime: Graphile keeps a permanently failed row for good and
         * nothing deletes it, so a running total would never return to zero.
         */
        exhausted: z.number().optional(),
    }),
    postgres: z.object({
        control: PostgresConnectionUsageSchema,
        apps: PostgresConnectionUsageSchema,
    }),
    pods: z.object({
        control_plane: z.object({
            // Totals across both tiers, so an older reader sees the whole control plane
            // rather than whichever half it happened to know about.
            ready_replicas: z.number(),
            desired_replicas: z.number(),
            /**
             * Restarts across the control plane over the last day. Absent until the
             * collector can compare two observations: a per-replica counter is
             * cumulative since the container started, so reporting it here would put a
             * lifetime figure under a 24-hour label, and reporting `0` would say no
             * restart happened when nothing looked.
             */
            restart_count_24h: z.number().optional(),
            cpu_usage_percent: z.number().optional(),
            memory_usage_mb: z.number().optional(),
            /**
             * One entry per live control-plane pod, never averaged into a single
             * figure: a rollout runs two incarnations at once and a mean hides
             * whichever one is in trouble. Absent when the pod read is unavailable.
             */
            replicas: z.array(ControlPlaneReplicaSchema).optional(),
            /** Absent when the cluster read is unavailable, never partially filled. */
            tiers: z
                .object({
                serve: ControlPlaneTierSchema,
                worker: ControlPlaneTierSchema,
            })
                .optional(),
            /**
             * The control plane could not read its own Deployments — a refused list, a
             * missing Deployment. The replica counts carry no information in that case,
             * so the page reports the read as broken rather than rendering them.
             */
            read_error: z.boolean().optional(),
        }),
    }),
    /**
     * Request and error counts per route family, beside `hotpath_latency` rather
     * than inside it: those keys end in `_ms`, and a count sitting in one of them
     * reads as milliseconds. A denial is a client error — `/auth/check` answering
     * "no" is the control plane working — so only `server_errors` counts what this
     * process got wrong. `latency_measured` says whether the family's
     * `hotpath_latency` bucket holds a reading: counts and percentiles cover
     * different windows, so traffic in the window does not imply a percentile.
     */
    hotpath_volume: z
        .record(z.object({
        requests: z.number(),
        server_errors: z.number(),
        client_errors: z.number(),
        latency_measured: z.boolean(),
    }))
        .optional(),
    provider_resource_health: z.record(ProviderResourceHealthStatusSchema),
    apps: z.object({
        count_by_status: z.record(z.number()),
        crash_loop_count: z.number(),
        unavailable_count: z.number(),
        restart_count_24h: z.number(),
    }),
    billing: z.object({
        window_start: z.string().datetime({ offset: true }),
        window_end: z.string().datetime({ offset: true }),
        app_running_minutes: z.array(z.object({
            app_id: z.string(),
            minutes: z.number(),
        })),
    }),
    updater: z.object({
        last_check_at: z.string().datetime({ offset: true }),
        last_check_result: z.string(),
        last_apply_at: z.string().datetime({ offset: true }).nullable(),
        /**
         * Whether the updater CronJob exists. `null` when the control plane cannot
         * read the updater namespace at all, which must not render as "off": an
         * install that opted out and one whose runs are wedged look identical
         * without this, and they are the two cases that need telling apart.
         */
        enabled: z.boolean().nullable().optional(),
        // Additive fields (same pattern as `crashes`): present in snapshots from a
        // collector that reads the deployed updater stack, absent in older rows so
        // existing readers keep parsing.
        installed_version: z.string().optional(),
        active_phase: z.string().nullable().optional(),
        last_apply_version: z.string().nullable().optional(),
        last_failure: UpdaterFailureSchema.nullable().optional(),
        /**
         * Start of the newest run that settled with a verdict. Unlike
         * `last_check_at`, a run still in flight or one that skipped never
         * advances it, so the fleet can tell whether a failure was answered.
         */
        last_answered_check_at: z.string().datetime({ offset: true }).nullable().optional(),
    }),
    hotpath_latency: z.object({
        auth_check_ms: LatencyBucketSchema,
        data_proxy_ms: LatencyBucketSchema,
        mcp_tool_ms: LatencyBucketSchema,
    }),
    // Unschedulable app pods and the aggregate over the nodes running apps. Present in snapshots from a
    // collector wired to a ComputeProvider; `null` when that read is unavailable
    // (best-effort sentinel); absent in pre-capacity snapshots (schema_version < 2).
    app_runtime_capacity: AppRuntimeCapacitySchema.nullable().optional(),
    // Every node in the cluster with its requests by owner — the Nodes card on
    // Internal Health. Local to the install: the fleet report strips it, like
    // `trends`. `null` when the capacity read was unavailable; absent in rows
    // written before the field existed.
    cluster_nodes: z.array(ClusterNodeSchema).nullable().optional(),
    // The renderer, ingress and certificates, read the way the primary server and
    // the async worker are. Local to the install: the fleet report strips it, like
    // `cluster_nodes`. `null` off-cluster; absent in rows written before it.
    platform_services: z.array(PlatformServiceSchema).nullable().optional(),
    // Bounded, deduplicated crash occurrences derived from pod termination states
    // (docs/11 §Crash summary). Additive optional field: present in snapshots from
    // a collector wired to a ComputeProvider (an empty array when nothing crashed),
    // absent in pre-crash snapshots so existing readers keep parsing.
    crashes: z.array(HealthCrashEntrySchema).optional(),
    // Upstream vendors whose outage presents as Greenlight misbehaving (docs/11
    // §Upstream status). Additive optional field: absent in snapshots from a
    // collector with no checker wired, which readers render as no data rather
    // than as everything being fine.
    upstream_status: UpstreamStatusSchema.optional(),
    // What this install brokers to (docs/11 §Fleet Health Reporting). Additive
    // optional field; `null` when the aggregate read failed, which stays distinct
    // from an install that genuinely has no integrations.
    integrations: HealthIntegrationsSchema.nullable().optional(),
    // One entry per scheduled worker task (docs/11 §Worker task status), with no
    // error text. Absent in rows written before it and when the status read failed.
    worker_tasks: WorkerTaskSummariesSchema.optional(),
    // Dashboard read-model extension: recent sample series keyed by metric name
    // (e.g. "queue_depth", "billing_minutes") for sparkline rendering — 60 1-min
    // buckets, one per stored snapshot. Not persisted with the row: the
    // health.latestSnapshot read handler derives it from the recent snapshot
    // window. Coordinate new metric keys with that
    // handler's trends builder.
    trends: z.record(z.array(z.number())).optional(),
});
// One audit-event category and its recent volume, for the "what gets recorded" view.
export const AuditCategoryCountSchema = z.object({
    category: z.enum([
        'data_access',
        'data_inspection',
        'auth',
        'agent',
        'pipeline',
        'permission',
        'app_env',
        'app_lifecycle',
        'admin',
        'resource',
        'ai_inference',
    ]),
    count: z.number(),
});
// Audit status for the Observability dashboard — covers the fields shown on
// the Audit Log Status view in docs/11-observability.md §Audit Log Status.
export const AuditStatusSchema = z.object({
    last_write_at: z.string().datetime({ offset: true }).nullable(),
    write_count_by_window: z.array(z.object({
        window: z.string(),
        count: z.number(),
    })),
    write_latency_p95_ms: z.number().nullable(),
    write_latency_p99_ms: z.number().nullable().optional(),
    // The org's audit retention window in days; null keeps the log indefinitely.
    retention_days: z.number().nullable(),
    oldest_entry_at: z.string().datetime({ offset: true }).nullable().optional(),
    worm_enabled: z.boolean(),
    compliance: z.array(z.object({
        framework: z.string(),
        status: z.enum(['ready', 'partial', 'not_ready']),
        detail: z.string(),
    })),
    // Last 60 one-minute write-count buckets, oldest→newest, for the throughput chart.
    throughput_per_min: z.array(z.number()).optional(),
    // 24h write volume split by category, drives the composition bar + list.
    events_by_category: z.array(AuditCategoryCountSchema).optional(),
});
//# sourceMappingURL=health.js.map